import React, { useState, useEffect, useMemo } from "react";
import {
  Sparkles, RefreshCw, Search, ArrowUpDown, ChevronLeft,
  Check, X, ExternalLink, ArrowRight, AlertCircle, Code,
  Wrench, Loader,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import api from "../api/client";
import ScoreRing from "./ScoreRing";

// Normalize URLs for matching: strip protocol, trailing slash, lowercase
const normalizeUrl = (u) => (u || "").replace(/^https?:\/\//, "").replace(/\/$/, "").toLowerCase();

const SCORE_LABELS = {
  title_score: "Title",
  meta_score: "Meta",
  h1_score: "H1",
  schema_score: "Schema",
  content_quality_score: "Content",
  og_score: "OG Tags",
};

function SerpPreview({ title, url, description, label }) {
  return (
    <div className="bg-dark/60 rounded-lg p-4 border border-dark-50">
      <div className="text-xs text-gray-500 mb-2 font-medium">{label}</div>
      <div className="text-blue-400 text-sm font-medium hover:underline cursor-pointer truncate">
        {title || "No title"}
      </div>
      <div className="text-green-500 text-xs truncate mt-0.5">{url}</div>
      <div className="text-gray-400 text-xs mt-1 line-clamp-2">
        {description || "No description"}
      </div>
    </div>
  );
}

function FixCard({ fix, onApprove, onReject, onPush }) {
  const statusColors = {
    suggested: "text-blue-400 bg-blue-900/20",
    approved: "text-green-400 bg-green-900/20",
    pushed: "text-copper bg-copper/10",
    rejected: "text-gray-500 bg-gray-800",
  };
  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono uppercase text-gray-400">{fix.fix_type} fix</span>
        <span className={`text-xs px-2 py-0.5 rounded ${statusColors[fix.status] || ""}`}>
          {fix.status}
        </span>
      </div>
      {fix.current_value && (
        <div>
          <div className="text-xs text-gray-500 mb-1">Current</div>
          <div className="text-sm text-gray-300 bg-dark/50 rounded p-2 font-mono text-xs break-all max-h-24 overflow-auto">
            {fix.current_value}
          </div>
        </div>
      )}
      <div>
        <div className="text-xs text-gray-500 mb-1">Suggested</div>
        <div className="text-sm text-green-300 bg-dark/50 rounded p-2 font-mono text-xs break-all max-h-24 overflow-auto">
          {fix.suggested_value}
        </div>
      </div>
      {fix.ai_reasoning && (
        <div className="text-xs text-gray-400 italic">{fix.ai_reasoning}</div>
      )}
      {fix.status === "suggested" && (
        <div className="flex gap-2">
          <button onClick={() => onApprove(fix.id)} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-green-900/30 text-green-400 hover:bg-green-900/50">
            <Check size={12} /> Approve
          </button>
          <button onClick={() => onReject(fix.id)} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-red-900/30 text-red-400 hover:bg-red-900/50">
            <X size={12} /> Reject
          </button>
        </div>
      )}
      {fix.status === "approved" && (
        <button onClick={() => onPush(fix.id)} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-copper/20 text-copper hover:bg-copper/30">
          <ArrowRight size={12} /> Push to WP
        </button>
      )}
    </div>
  );
}

export default function SeoOptimizer({ selectedSite }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [scoreFilter, setScoreFilter] = useState("all");
  const [sortDir, setSortDir] = useState("asc");
  const [pushingSchema, setPushingSchema] = useState(false);
  const [schemaResult, setSchemaResult] = useState(null);
  const [progress, setProgress] = useState(null);
  const [deepLinkError, setDeepLinkError] = useState("");

  const fetchPages = (silent) => {
    if (!selectedSite) return;
    if (!silent) setLoading(true);
    api.get(`/websites/${selectedSite.id}/seo/?page_size=500`)
      .then((res) => {
        setPages(res.data.results || res.data);
        if (res.data.progress) setProgress(res.data.progress);
      })
      .catch(() => setPages([]))
      .finally(() => { if (!silent) setLoading(false); });
  };

  useEffect(() => { fetchPages(); }, [selectedSite]);

  // Deep-link: ?url=<page_url> or ?post_id=<id> auto-selects the page
  useEffect(() => {
    if (loading || !pages.length || selectedPage) return;
    const targetUrl = searchParams.get("url");
    const targetPostId = searchParams.get("post_id");
    if (!targetUrl && !targetPostId) return;

    let match = null;
    if (targetUrl) {
      const norm = normalizeUrl(targetUrl);
      match = pages.find((p) => normalizeUrl(p.url) === norm);
    } else if (targetPostId) {
      match = pages.find((p) => String(p.wp_post_id) === String(targetPostId));
    }

    if (match) {
      setSelectedPage(match.id);
      setDeepLinkError("");
    } else {
      setDeepLinkError(
        `Page not found in SEO Optimizer: ${targetUrl || `post #${targetPostId}`}. Run "Analyze All Pages" first.`
      );
    }
    // Clear the query param so refresh doesn't re-trigger
    setSearchParams({}, { replace: true });
  }, [loading, pages, searchParams, selectedPage, setSearchParams]);

  // Poll while analysis is in progress
  useEffect(() => {
    if (!progress || progress.pending <= 0) return;
    const timer = setInterval(() => fetchPages(true), 5000);
    return () => clearInterval(timer);
  }, [progress, selectedSite]);

  useEffect(() => {
    if (!selectedPage || !selectedSite) return;
    setDetailLoading(true);
    api.get(`/websites/${selectedSite.id}/seo/${selectedPage}/`)
      .then((res) => setDetail(res.data))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  }, [selectedPage, selectedSite]);

  const analyzeAll = () => {
    setAnalyzing(true);
    api.post(`/websites/${selectedSite.id}/seo/analyze/`)
      .then(() => { setTimeout(fetchPages, 3000); })
      .finally(() => setAnalyzing(false));
  };

  const pushSchema = async () => {
    if (!selectedSite || pushingSchema) return;
    setPushingSchema(true);
    setSchemaResult(null);
    try {
      await api.post(`/websites/${selectedSite.id}/schema/generate-push/`, {
        schema_type: "general_contractor",
      });
      setSchemaResult({ success: true, message: "Schema pushed to WordPress" });
      // Refresh detail to show updated schema
      if (selectedPage) {
        const res = await api.get(`/websites/${selectedSite.id}/seo/${selectedPage}/`);
        setDetail(res.data);
      }
    } catch (err) {
      setSchemaResult({
        success: false,
        message: err.response?.data?.error || "Failed to push schema",
      });
    } finally {
      setPushingSchema(false);
    }
  };

  const handleFixAction = (action, fixId) => {
    api.post(`/websites/${selectedSite.id}/seo/${selectedPage}/fixes/${fixId}/${action}/`)
      .then(() => {
        api.get(`/websites/${selectedSite.id}/seo/${selectedPage}/`)
          .then((res) => setDetail(res.data));
      });
  };

  const filtered = useMemo(() => {
    let data = [...pages];
    if (search) data = data.filter((p) => p.url.toLowerCase().includes(search.toLowerCase()));
    if (scoreFilter === "critical") data = data.filter((p) => p.overall_score < 50);
    else if (scoreFilter === "needs_work") data = data.filter((p) => p.overall_score >= 50 && p.overall_score < 80);
    else if (scoreFilter === "good") data = data.filter((p) => p.overall_score >= 80);
    data.sort((a, b) => sortDir === "asc" ? a.overall_score - b.overall_score : b.overall_score - a.overall_score);
    return data;
  }, [pages, search, scoreFilter, sortDir]);

  const analyzing_count = progress ? progress.pending : pages.filter((p) => p.status === "analyzing" || p.status === "pending").length;
  const complete_count = progress ? progress.complete : pages.filter((p) => p.status === "complete").length;
  const total_count = progress ? progress.total : pages.length;

  if (!selectedSite) return <div className="text-gray-500 text-center py-20">Select a website</div>;

  // Detail View
  if (selectedPage && detail) {
    return (
      <div className="space-y-6">
        <button onClick={() => { setSelectedPage(null); setDetail(null); }} className="flex items-center gap-2 text-sm text-gray-400 hover:text-white">
          <ChevronLeft size={16} /> Back to list
        </button>

        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold truncate max-w-2xl">{detail.url}</h2>
            <div className="text-xs text-gray-500 mt-1">Keyword: {detail.primary_keyword || "auto-detected"}</div>
          </div>
          <ScoreRing score={detail.overall_score} size={72} label="Overall" />
        </div>

        {/* Score breakdown */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {Object.entries(SCORE_LABELS).map(([key, label]) => (
            <div key={key} className="flex flex-col items-center">
              <ScoreRing score={detail[key] || 0} size={56} strokeWidth={4} label={label} />
            </div>
          ))}
        </div>

        {/* SERP Preview */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SerpPreview label="Current" title={detail.title_tag} url={detail.url} description={detail.meta_description} />
          {detail.fixes?.find((f) => f.fix_type === "title" || f.fix_type === "meta") && (
            <SerpPreview
              label="After Fixes"
              title={detail.fixes.find((f) => f.fix_type === "title")?.suggested_value || detail.title_tag}
              url={detail.url}
              description={detail.fixes.find((f) => f.fix_type === "meta")?.suggested_value || detail.meta_description}
            />
          )}
        </div>

        {/* Element details */}
        <div className="space-y-4">
          {/* Title */}
          <div className="bg-dark-300 border border-dark-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold">Title Tag</h3>
              <span className="text-xs font-mono" style={{ color: detail.title_score >= 80 ? "#22c55e" : detail.title_score >= 50 ? "#f59e0b" : "#ef4444" }}>
                {detail.title_score}/100
              </span>
            </div>
            <div className="text-sm text-gray-300 font-mono bg-dark/50 rounded p-2 mb-2">{detail.title_tag || "—"}</div>
            {detail.title_suggestion && <div className="text-xs text-gray-400">{detail.title_suggestion}</div>}
          </div>

          {/* Meta */}
          <div className="bg-dark-300 border border-dark-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold">Meta Description</h3>
              <span className="text-xs font-mono" style={{ color: detail.meta_score >= 80 ? "#22c55e" : detail.meta_score >= 50 ? "#f59e0b" : "#ef4444" }}>
                {detail.meta_score}/100
              </span>
            </div>
            <div className="text-sm text-gray-300 font-mono bg-dark/50 rounded p-2 mb-2">{detail.meta_description || "—"}</div>
            {detail.meta_suggestion && <div className="text-xs text-gray-400">{detail.meta_suggestion}</div>}
          </div>

          {/* Schema */}
          <div className="bg-dark-300 border border-dark-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold flex items-center gap-2"><Code size={14} /> Schema Markup</h3>
              <span className="text-xs font-mono" style={{ color: detail.schema_score >= 80 ? "#22c55e" : detail.schema_score >= 50 ? "#f59e0b" : "#ef4444" }}>
                {detail.schema_score}/100
              </span>
            </div>
            {detail.schema_markup?.length > 0 ? (
              <div className="text-xs text-gray-400">
                Types found: {(() => {
                  const types = new Set();
                  (detail.schema_markup || []).forEach((s) => {
                    if (s["@type"]) types.add(s["@type"]);
                    (s["@graph"] || []).forEach((g) => { if (g["@type"]) types.add(g["@type"]); });
                  });
                  return [...types].join(", ") || "Unknown";
                })()}
              </div>
            ) : (
              <div className="text-xs text-gray-400">
                {detail.schema_score >= 50
                  ? "Sitewide schema active (detected from homepage)"
                  : <span className="text-red-400">No schema markup detected</span>}
              </div>
            )}
            {detail.schema_suggestion?.[0] && (
              <div className="text-xs text-gray-500 mt-1">{detail.schema_suggestion[0]}</div>
            )}
            {detail.schema_score < 80 && (
              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={pushSchema}
                  disabled={pushingSchema}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-copper/20 hover:bg-copper/30 text-copper text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {pushingSchema ? <Loader size={12} className="animate-spin" /> : <Wrench size={12} />}
                  {pushingSchema ? "Pushing..." : "Generate & Push Schema"}
                </button>
                {schemaResult && (
                  <span className={`text-xs ${schemaResult.success ? "text-green-400" : "text-red-400"}`}>
                    {schemaResult.message}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Content stats */}
          <div className="bg-dark-300 border border-dark-50 rounded-xl p-4">
            <h3 className="text-sm font-semibold mb-3">Content Analysis</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><span className="text-gray-500">Words:</span> <span className="font-mono">{detail.word_count}</span></div>
              <div><span className="text-gray-500">Images:</span> <span className="font-mono">{detail.images_total}</span></div>
              <div><span className="text-gray-500">Missing Alt:</span> <span className="font-mono text-amber-400">{detail.images_missing_alt}</span></div>
              <div><span className="text-gray-500">Int Links:</span> <span className="font-mono">{detail.internal_links}</span></div>
            </div>
          </div>
        </div>

        {/* Fixes */}
        {detail.fixes?.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-300">AI Suggestions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {detail.fixes.map((fix) => (
                <FixCard
                  key={fix.id}
                  fix={fix}
                  onApprove={(id) => handleFixAction("approve", id)}
                  onReject={(id) => handleFixAction("reject", id)}
                  onPush={(id) => handleFixAction("push", id)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Sparkles size={20} className="text-copper" /> SEO Optimizer
        </h2>
        <button
          onClick={analyzeAll}
          disabled={analyzing}
          className="flex items-center gap-2 px-4 py-2 bg-copper text-dark text-sm font-semibold rounded-lg hover:bg-copper-400 disabled:opacity-50"
        >
          <RefreshCw size={14} className={analyzing ? "animate-spin" : ""} />
          {analyzing ? "Starting..." : "Analyze All Pages"}
        </button>
      </div>

      {analyzing_count > 0 && (
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-300">Analyzing pages...</span>
            <span className="text-xs font-mono text-copper">{complete_count} of {total_count}</span>
          </div>
          <div className="w-full bg-dark-50 rounded-full h-2">
            <div className="bg-copper h-2 rounded-full transition-all" style={{ width: `${total_count ? (complete_count / total_count) * 100 : 0}%` }} />
          </div>
        </div>
      )}

      {deepLinkError && (
        <div className="flex items-start gap-2 bg-amber-900/15 border border-amber-900/30 rounded-xl p-3 text-xs text-amber-300">
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
          <span className="flex-1">{deepLinkError}</span>
          <button onClick={() => setDeepLinkError("")} className="text-amber-400 hover:text-amber-200">
            <X size={12} />
          </button>
        </div>
      )}

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input type="text" placeholder="Search pages..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-dark-300 border border-dark-50 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-copper/50" />
        </div>
        <div className="flex bg-dark-300 rounded-lg border border-dark-50 overflow-hidden">
          {[
            { key: "all", label: "All" },
            { key: "critical", label: "Critical <50" },
            { key: "needs_work", label: "Needs Work" },
            { key: "good", label: "Good 80+" },
          ].map((f) => (
            <button key={f.key} onClick={() => setScoreFilter(f.key)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${scoreFilter === f.key ? "bg-copper/20 text-copper" : "text-gray-400 hover:text-white"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <button onClick={() => setSortDir(sortDir === "asc" ? "desc" : "asc")} className="p-2 text-gray-400 hover:text-white">
          <ArrowUpDown size={14} />
        </button>
      </div>

      <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500 animate-pulse">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 border-b border-dark-50">
                  <th className="text-left py-2.5 px-4 font-medium">URL</th>
                  <th className="py-2.5 px-3 font-medium">Score</th>
                  <th className="py-2.5 px-3 font-medium">Title</th>
                  <th className="py-2.5 px-3 font-medium">Meta</th>
                  <th className="py-2.5 px-3 font-medium">Schema</th>
                  <th className="py-2.5 px-3 font-medium">Content</th>
                  <th className="py-2.5 px-3 font-medium">Fixes</th>
                  <th className="py-2.5 px-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((page) => (
                  <tr key={page.id} onClick={() => setSelectedPage(page.id)}
                    className="border-b border-dark-50/50 hover:bg-dark-50/30 transition-colors cursor-pointer">
                    <td className="py-2.5 px-4 text-gray-300 font-mono text-xs truncate max-w-xs">{page.url.replace(/^https?:\/\//, "")}</td>
                    <td className="py-2.5 px-3 text-center">
                      <ScoreRing score={page.overall_score} size={36} strokeWidth={3} />
                    </td>
                    <td className="py-2.5 px-3 text-center font-mono text-xs" style={{ color: page.title_score >= 80 ? "#22c55e" : page.title_score >= 50 ? "#f59e0b" : "#ef4444" }}>
                      {page.title_score}
                    </td>
                    <td className="py-2.5 px-3 text-center font-mono text-xs" style={{ color: page.meta_score >= 80 ? "#22c55e" : page.meta_score >= 50 ? "#f59e0b" : "#ef4444" }}>
                      {page.meta_score}
                    </td>
                    <td className="py-2.5 px-3 text-center font-mono text-xs" style={{ color: page.schema_score >= 80 ? "#22c55e" : page.schema_score >= 50 ? "#f59e0b" : "#ef4444" }}>
                      {page.schema_score}
                    </td>
                    <td className="py-2.5 px-3 text-center font-mono text-xs" style={{ color: page.content_quality_score >= 80 ? "#22c55e" : page.content_quality_score >= 50 ? "#f59e0b" : "#ef4444" }}>
                      {page.content_quality_score}
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      {page.fix_count > 0 && <span className="text-xs bg-copper/20 text-copper px-2 py-0.5 rounded">{page.fix_count}</span>}
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        page.status === "complete" ? "bg-green-900/20 text-green-400" :
                        page.status === "analyzing" ? "bg-blue-900/20 text-blue-400" :
                        page.status === "error" ? "bg-red-900/20 text-red-400" :
                        "bg-gray-800 text-gray-400"
                      }`}>{page.status}</span>
                    </td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr><td colSpan={8} className="py-12 text-center text-gray-500">
                    {pages.length ? "No pages match your filters" : "No pages analyzed yet — click 'Analyze All Pages' to start"}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
